import { ERROR_CODES, HTTP_STATUS } from '../constants';

type AxiosErrorLike = {
  isAxiosError?: boolean;
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
};

const isAxiosError = (error: unknown): error is AxiosErrorLike =>
  typeof error === 'object' &&
  error !== null &&
  (error as AxiosErrorLike).isAxiosError === true;

export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (!isAxiosError(error)) {
    return error instanceof Error && error.message.trim()
      ? error.message.trim()
      : fallback;
  }

  const data = error.response?.data as Record<string, unknown> | undefined;
  const raw = data?.message;

  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim();
  }

  if (
    raw &&
    typeof raw === 'object' &&
    'message' in raw &&
    typeof (raw as { message: unknown }).message === 'string'
  ) {
    const nested = (raw as { message: string }).message.trim();
    if (nested) return nested;
  }

  if (Array.isArray(raw)) {
    const first = raw.find((x) => typeof x === 'string') as string | undefined;
    if (first?.trim()) return first.trim();
  }

  if (
    typeof error.message === 'string' &&
    error.message.trim() &&
    !/^Request failed with status code \d+$/i.test(error.message)
  ) {
    return error.message.trim();
  }

  return fallback;
};

export const getApiErrorCode = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) return undefined;
  const data: any = error.response?.data;
  return data?.message?.code;
};

export const isInvalidLineSessionError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;

  const status = error.response?.status;
  if (status !== HTTP_STATUS.UNAUTHORIZED) return false;

  const code = getApiErrorCode(error);
  return code === ERROR_CODES.INVALID_LINE_SESSION_TOKEN;
};
